import React from 'react';
import { useParams } from 'react-router';
import { NetworkStatus } from '@apollo/client';
import type { TabConfig } from '@odin-labs/components';
import { ScrollSpyTabsPages, useScrollSpyTabs } from 'components/tabs';
import { NewHeader } from 'components/header/NewHeader';
import type { NewHeaderActionsProps } from 'components/header/types';
import { RouterPrompt } from 'components/routerPrompt';
import { AuthContext } from 'auth';
import { evalJsCode, useDeferredFormSubmission, useIsMounted } from 'utils';
import { CheckIcon } from 'components/icons';
import { Container } from 'components/container';
import { useGetJobsiteFormSubmissionQuery } from 'apollo/generated/client-operations';
import type {
  FormSubmissionTabDefinition,
  FormSubmissionTabProps,
  FormSubmissionTabWithRefProps,
  FormSubmissionTitleDefinition,
} from './types';
import { FormSubmissionEdit } from './tabs/FormSubmissionEdit';
import { FormSubmissionTitle } from './components/FormSubmissionTitle';
import { getEvalContext } from './tabs/FormSubmissionEdit.forms';
import { useDependencies } from './utils';

export const classes = {
  container: 'odin-h-full',
  tabPage: 'odin-overflow-y-scroll odin-bg-white odin-h-full',
  headerTitleContainer: 'odin-flex odin-space-x-5 odin-items-center',
  headerTitleBadgesContainer: 'odin-space-x-2',
};

export const getTabsConfig = (
  tabs: FormSubmissionTabDefinition,
): TabConfig<FormSubmissionTabProps>[] => {
  return Object.entries(tabs ?? {}).map(
    ([name, tabDef], index): TabConfig<FormSubmissionTabProps> => ({
      name,
      text: typeof tabDef === 'string' ? tabDef : tabDef.text,
      badge: typeof tabDef === 'string' ? '' : tabDef.badge,
      relativePath: index === 0 ? '' : `#${name}`,
      component: FormSubmissionEdit,
    }),
  );
};

export function FormSubmissionContainer(): React.ReactElement {
  const { formType, formSubmissionId } = useParams<{
    formType: string;
    formSubmissionId: string;
  }>();
  const [tabsDefinition, setTabsDefinition] =
    React.useState<FormSubmissionTabDefinition>({});
  const [titleConfig, setTitleConfig] =
    React.useState<FormSubmissionTitleDefinition>({ items: null });
  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = React.useState(false);
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  const { formRef, deferredSubmission, submitForm } =
    useDeferredFormSubmission();

  const { currentUser: user } = React.useContext(AuthContext);

  const baseRoute = `/forms/${formType}/${formSubmissionId}`;
  const tabsConfig = React.useMemo(
    () => getTabsConfig(tabsDefinition),
    [tabsDefinition],
  );

  const { data, loading, refetch, networkStatus } =
    useGetJobsiteFormSubmissionQuery({
      variables: { id: formSubmissionId },
      fetchPolicy: 'no-cache',
      skip: !formSubmissionId,
    });

  const jobsiteFormSubmission = data?.getJobsiteFormSubmission;

  const {
    dependencies: dependenciesExpressions,
    tabs: tabsExpression,
    title: titleExpression,
  } = jobsiteFormSubmission?.jobsiteForm.form.content.edit ?? {};

  const dependencies = useDependencies(dependenciesExpressions);

  React.useEffect(() => {
    if (dependencies && tabsExpression) {
      const evalContext = getEvalContext({
        dependencies,
        user,
        jobsiteFormSubmission,
      });
      const computedTabs = evalJsCode<FormSubmissionTabDefinition>(
        tabsExpression,
        evalContext,
      );
      setTabsDefinition(computedTabs);
    }
  }, [dependencies, tabsExpression]);

  React.useEffect(() => {
    if (dependencies && titleExpression) {
      const evalContext = getEvalContext({
        dependencies,
        user,
        jobsiteFormSubmission,
      });
      const computedTitle = evalJsCode<FormSubmissionTitleDefinition>(
        titleExpression,
        evalContext,
      );
      setTitleConfig(computedTitle);
    }
  }, [dependencies, titleExpression]);

  const headerActionsProps = React.useMemo<NewHeaderActionsProps>(
    () => ({
      menuItems: [
        {
          onClick: submitForm,
          text: 'Save Changes',
          hideTextOnMobile: false,
          withSpinner: isSaving,
          icon: CheckIcon,
          disabled: !isFormDirty,
        },
      ],
      baseRoute,
      headerActions: ['back', 'print'],
    }),
    [submitForm, isFormDirty, isSaving],
  );

  const tabsPageProps = React.useMemo(
    (): FormSubmissionTabWithRefProps => ({
      loading,
      jobsiteFormSubmission,
      onIsDirtyChange: setIsFormDirty,
      setTabsDefinition,
      ref: formRef,
      deferredSubmission,
      refetchJobsiteFormSubmission: refetch,
      isSaving,
      setIsSaving: (value: boolean): void => {
        if (isMounted()) {
          setIsSaving(value);
          if (!value) setIsFormDirty(false);
        }
      },
    }),
    [
      loading,
      jobsiteFormSubmission,
      deferredSubmission,
      setIsFormDirty,
      formRef,
      refetch,
      isSaving,
      setIsSaving,
      isMounted,
    ],
  );

  const { tabs, currentTab } = useScrollSpyTabs({
    tabsConfig,
    baseRoute,
    loading: networkStatus === NetworkStatus.loading,
  });

  return (
    <Container className={classes.container}>
      <RouterPrompt when={isFormDirty} onConfirm={submitForm} />
      <NewHeader
        title={
          <FormSubmissionTitle
            jobsiteFormSubmission={jobsiteFormSubmission}
            titleConfig={titleConfig}
          />
        }
        tabsProps={tabsConfig.length ? { tabs, currentTab } : null}
        actionsProps={headerActionsProps}
      />
      {!!tabsConfig.length && (
        <ScrollSpyTabsPages
          className={classes.tabPage}
          baseRoute={baseRoute}
          tabs={tabs}
          componentProps={tabsPageProps}
        />
      )}
    </Container>
  );
}
